import { type FC, useMemo } from "react";

import { ChatsCircleIcon } from "@phosphor-icons/react";
import moment from "moment";

import { Badge, Modal, Spinner, Text } from "@alpacahq/ui/old";

import { useGetAcatsTransferAssets } from "~/v2/api/hooks/useGetAcatsTransferAssets";
import { useGetAcatsTransferDetails } from "~/v2/api/hooks/useGetAcatsTransferDetails";
import type {
  AcatsTransfer,
  ContraBroker,
  DtccTransferType,
} from "~/v2/api/rest/acats";
import { ACATS_TRANSFER_STATUS_BADGES } from "~/v2/constants";
import { getPrettyCash } from "~/v2/utils/formatting";
import { renderTransferDirection } from "./utils";

/**
 * Helper function to determine if a transfer is a partial transfer
 * based on the DTCC transfer type codes
 */
const isPartialTransferType = (transferType: DtccTransferType): boolean => {
  return transferType === "PTD" || transferType === "PTR";
};

type TransferDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  transfer: AcatsTransfer | null;
  accountId: string;
  contraBrokers: ContraBroker[];
};

type TransferDetailsModalContentProps = {
  transfer: AcatsTransfer;
  accountId: string;
  contraBrokers: ContraBroker[];
  isOpen: boolean;
};

const TransferDetailsModalContent: FC<TransferDetailsModalContentProps> = ({
  transfer,
  accountId,
  contraBrokers,
  isOpen,
}) => {
  const isPartialTransfer = transfer?.transfer_type
    ? isPartialTransferType(transfer.transfer_type)
    : false;

  // Create a mapping from DTC number to broker name from the passed prop
  const brokerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    contraBrokers.forEach((broker) => {
      map.set(broker.number, broker.name);
    });

    return map;
  }, [contraBrokers]);

  const {
    data: transferDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
  } = useGetAcatsTransferDetails({
    accountId,
    acatsId: transfer?.acats_id || "",
    options: {
      enabled: isOpen && !!transfer?.acats_id,
    },
  });

  const {
    data: transferAssets,
    isLoading: isLoadingAssets,
    error: assetsError,
  } = useGetAcatsTransferAssets({
    accountId,
    acatsId: transfer?.acats_id || "",
    options: {
      enabled: isOpen && !!transfer?.acats_id && isPartialTransfer,
    },
  });

  const isLoading = isLoadingDetails;
  const hasError = detailsError;

  const details = transferDetails || transfer;
  const statusConfig =
    ACATS_TRANSFER_STATUS_BADGES[details.status.transfer_status];

  // Format date as shown in design
  const formatDate = (dateString: string) => {
    return moment(dateString).format("MMM Do, YYYY");
  };

  // Memoize the options objects to prevent recreating them on every render
  const fromOptionsModal = useMemo(
    () => ({
      direction: "FROM" as const,
      textStyles: {
        brokerName: "text-sm font-medium",
        accountInfo: "text-sm font-normal text-gray-700",
      },
      brokerNameMap,
    }),
    [brokerNameMap],
  );

  const toOptionsModal = useMemo(
    () => ({
      direction: "TO" as const,
      textStyles: {
        brokerName: "text-sm font-medium text-gray-700",
        accountInfo: "text-sm text-gray-600",
      },
      brokerNameMap,
    }),
    [brokerNameMap],
  );

  const FromToSection: FC = () => {
    return (
      <div className="grid grid-cols-2 gap-10">
        <div>
          <Text className="mb-2 font-bold text-gray-700 text-xs">From</Text>
          <div className="grid">
            {renderTransferDirection(details, fromOptionsModal)}
          </div>
        </div>

        <div>
          <Text className="mb-2 font-bold text-gray-700 text-xs">To</Text>
          <div className="grid">
            {renderTransferDirection(details, toOptionsModal)}
          </div>
        </div>
      </div>
    );
  };

  const StatusSection: FC = () => (
    <div className="grid grid-cols-3 gap-8">
      <div>
        <Text className="mb-2 font-bold text-gray-700 text-xs">
          Transfer Status
        </Text>
        <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
      </div>
      <div>
        <Text className="mb-2 font-bold text-gray-700 text-xs">
          Transfer Date
        </Text>
        <Text className="font-normal text-sm">
          {formatDate(details.created_at)}
        </Text>
      </div>
      <div>
        <Text className="mb-2 font-bold text-gray-700 text-xs">
          Settled Date
        </Text>
        <Text className="font-normal text-sm">
          {details.settlement_date ? formatDate(details.settlement_date) : "-"}
        </Text>
      </div>
    </div>
  );

  const AccountHolderDetail: FC = () => (
    <div>
      <Text className="mb-2 font-bold text-gray-700 text-xs">
        Account Holder Detail
      </Text>
      <div className="grid grid-cols-2 gap-8">
        <div>
          <Text className="mb-1 font-normal text-gray-500 text-sm">
            Legal Name
          </Text>
          <Text className="font-medium text-gray-700 text-sm">
            {details.account_name || "-"}
          </Text>
        </div>
      </div>
    </div>
  );

  const TransferAmount: FC = () => {
    // Handle assets loading state
    if (isLoadingAssets) {
      return (
        <div>
          <Text className="mb-4 font-medium text-gray-600 text-xs">
            Transfer Amount
          </Text>
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        </div>
      );
    }

    // Handle assets error state
    if (assetsError) {
      return (
        <div>
          <Text className="mb-2 font-bold text-gray-700 text-xs">
            Transfer Amount
          </Text>
          <div className="rounded-lg bg-yellow-50 p-4">
            <Text className="text-sm text-yellow-700">
              Unable to load transfer amount details at this time.
            </Text>
          </div>
        </div>
      );
    }

    // For full transfers, show generic amounts
    if (!isPartialTransfer) {
      return (
        <div>
          <Text className="mb-2 font-bold text-gray-700 text-xs">
            Transfer Amount
          </Text>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Text className="font-normal text-gray-500 text-sm">
                Cash Balance
              </Text>
              <Text className="font-family-mono font-medium text-gray-700 text-sm">
                Entire Balance
              </Text>
            </div>
            <div className="flex items-center justify-between">
              <Text className="font-normal text-gray-500 text-sm">
                Equity Amount
              </Text>
              <Text className="font-medium text-gray-700 text-sm">
                Entire Balance
              </Text>
            </div>
          </div>
        </div>
      );
    }

    // For partial transfers, show asset details
    if (!transferAssets || transferAssets.length === 0) {
      return (
        <div>
          <Text className="mb-2 font-bold text-gray-700 text-xs">
            Transfer Amount
          </Text>
          <Text className="text-gray-600 text-sm">
            No specific assets listed for this transfer.
          </Text>
        </div>
      );
    }

    // Calculate totals
    const cashAssets = transferAssets.filter(
      (asset) => asset.asset_class === "CASH",
    );
    const equityAssets = transferAssets.filter(
      (asset) => asset.asset_class === "EQUITY",
    );

    const totalCash = cashAssets.reduce(
      (sum, asset) => sum + (asset.amount || 0),
      0,
    );

    return (
      <div>
        <Text className="mb-2 font-bold text-gray-700 text-xs">
          Transfer Amount
        </Text>
        <div className="grid gap-3">
          <div className="flex items-center justify-between">
            <Text className="font-normal text-gray-500 text-sm">
              Cash Balance
            </Text>
            <Text className="font-medium text-gray-700 text-sm">
              {getPrettyCash(totalCash)}
            </Text>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Text className="font-normal text-gray-500 text-sm">
                Equity Amount
              </Text>
              <Text className="font-medium text-gray-700 text-sm"></Text>
            </div>

            {/* Show individual equity positions with no gap for first item */}
            <div className="mt-0 grid gap-3">
              {equityAssets.map((asset) => (
                <div
                  key={[
                    asset.symbol,
                    asset.position,
                    asset.qty,
                    asset.amount,
                    asset.asset_description,
                  ]
                    .filter((part) => part != null && part !== "")
                    .join("|")}
                  className="grid grid-cols-[150px_1fr_auto] items-center pl-4"
                >
                  <div></div>
                  <div className="flex items-center gap-2">
                    <Text className="text-gray-500 text-xs">
                      {asset.symbol ||
                        asset.asset_description ||
                        "Equity Asset"}
                    </Text>
                  </div>
                  <Text className="text-sm">
                    {asset.qty
                      ? `${asset.qty} Share${
                          parseInt(asset.qty, 10) !== 1 ? "s" : ""
                        } (${asset.position === "LONG" ? "Long" : "Short"})`
                      : asset.amount
                        ? getPrettyCash(asset.amount)
                        : "Full Amount (Long)"}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid max-h-[70vh] gap-5 overflow-y-auto">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : hasError ? (
        <div className="rounded-lg bg-red-50 p-4">
          <Text className="text-red-600 text-sm">
            Failed to load transfer details. Please try again.
          </Text>
        </div>
      ) : (
        <>
          <FromToSection />
          <hr className="m-0 border-gray-200" />
          <StatusSection />
          {details.account_name && (
            <>
              <hr className="m-0 border-gray-200" />
              <AccountHolderDetail />
            </>
          )}
          <hr className="m-0 border-gray-200" />
          <TransferAmount />

          <div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <ChatsCircleIcon size={20} />
              <Text className="text-gray-600 text-xs">
                For any questions about your transfer, contact{" "}
                <a
                  href="mailto:support@alpaca.markets"
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  support@alpaca.markets
                </a>
              </Text>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const TransferDetailsModal: FC<TransferDetailsModalProps> = ({
  isOpen,
  onClose,
  transfer,
  accountId,
  contraBrokers,
}) => {
  // Early return before any hooks to keep React happy
  if (!transfer) {
    return null;
  }

  return (
    <Modal
      size="sm"
      title="Transfer Detail"
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="p-5"
      contentClassName="flex w-full flex-col justify-center space-y-2" // Use of space-y required to fix the layout issue
    >
      <TransferDetailsModalContent
        transfer={transfer}
        accountId={accountId}
        contraBrokers={contraBrokers}
        isOpen={isOpen}
      />
    </Modal>
  );
};
